import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTenantWarehouses, type Warehouse } from '../../api/platformTenants';

export interface WarehousePickerProps {
  tenantId: number | null;
  value: number | null;
  onChange: (warehouseId: number) => void;
}

/**
 * Warehouse picker (depends on a selected tenant). Logic unchanged; restyled
 * with Bootstrap/Velzon dropdown classes. Keeps listbox/option roles.
 */
export function WarehousePicker({ tenantId, value, onChange }: WarehousePickerProps) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const { data, isLoading, error } = useTenantWarehouses(tenantId, token);
  const [open, setOpen] = useState(false);

  const warehouses = data?.data ?? [];
  const selected = warehouses.find((w) => w.id === value) ?? null;
  const disabled = tenantId === null;

  return (
    <div className="dropdown position-relative" style={{ minWidth: 200 }}>
      <button
        type="button"
        className="btn btn-soft-secondary btn-sm dropdown-toggle"
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Warehouse"
        disabled={disabled}
      >
        {disabled ? 'Warehouse' : isLoading ? 'Caricamento…' : selected ? selected.code : 'Warehouse'}
      </button>
      <ul
        role="listbox"
        hidden={!open || disabled}
        className={'dropdown-menu' + (open && !disabled ? ' show' : '')}
        style={{ display: open && !disabled ? 'block' : 'none', maxHeight: 280, overflowY: 'auto', minWidth: 220 }}
      >
        {error && <li className="dropdown-item-text text-danger">Errore: {String(error)}</li>}
        {warehouses.map((w: Warehouse) => (
          <li key={w.id}>
            <button
              type="button"
              role="option"
              aria-selected={w.id === value}
              className={'dropdown-item' + (w.id === value ? ' active' : '')}
              onClick={() => { onChange(w.id); setOpen(false); }}
            >
              <span className="font-monospace">{w.code}</span>
              <span className="d-block text-muted small">{w.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
